import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUploadPolling } from '../contexts/UploadPollingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Label from '../components/ui/Label';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import Modal from '../components/ui/Modal';
import Button, { ROW_ICON_CLASS } from '../components/ui/Button';
import FileDropzone from '../components/ui/FileDropzone';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import { toast } from 'react-hot-toast';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../services/permissions';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';
import {
  defaultUniverseColumnMappings,
  firstCsvLine,
  getUniverseDuplicateColumnError,
  getUniverseFileColumnChoices,
  parseCsvRow,
  universeCsvUsesNamedHeader,
} from '../utils/universeColumnMapping';
import { MAX_UPLOAD_FILE_BYTES } from '../utils/uploadConstants';
import { formatS3DirectUploadError, uploadBrowserFileToS3 } from '../utils/s3PresignedUpload';
import { ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, Loader2, Upload as UploadIcon, FileText, Info } from 'lucide-react';

/** Values for Upload History filter (independent of the main Upload type control). */
const UPLOAD_HISTORY_TYPE_FILTER = { all: 'all', universe: 'universe', fast_id: 'fast_id', bulk: 'bulk' };
const HISTORY_FETCH_LIMIT = 200;
// Allowed extensions for Bulk upload (lowercase)
const BULK_ALLOWED_EXTENSIONS = ['.json', '.csv'];
// Schema fields for Bulk column mapping (order preserved)
const BULK_SCHEMA_FIELDS = [
  'email',
  'email_md5',
  'first_name',
  'last_name',
  'gender',
  'dob',
  'phone_number',
  'optin_ip_address',
  'optin_site',
  'address',
  'city',
  'state',
  'zipcode',
  'optin_date',
];
// Bulk column mapping: these must stay checked and mapped (user cannot disable)
const BULK_ALWAYS_ENABLED_FIELDS = new Set(['email', 'optin_ip_address', 'optin_site', 'optin_date']);
/** Default checkbox state: required fields on, optional fields off (`bulkFieldEnabled[field] === true` means checked). */
const defaultBulkFieldEnabled = () =>
  BULK_SCHEMA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: BULK_ALWAYS_ENABLED_FIELDS.has(f) }), {});
// File Information fields (stored in bulk_file_upload via file_upload; text inputs in File Information section)
const BULK_METADATA_FIELDS = [
  'supplier_name',
  'purchase_date',
  'quality',
  'domains',
  'cost',
  'agreement',
];
const BULK_METADATA_FIELD_LABELS = {
  supplier_name: 'Supplier name',
  purchase_date: 'Purchase date',
  quality: 'Quality',
  domains: 'Domains',
  cost: 'Cost',
  agreement: 'Agreement',
};
/** Column mapping keys in display order (Bulk upload details dialog). */
const BULK_COLUMN_DETAIL_KEYS = [
  'email_field',
  'email_md5_field',
  'first_name_field',
  'last_name_field',
  'gender_field',
  'dob_field',
  'phone_number_field',
  'optin_ip_address_field',
  'optin_site_field',
  'address_field',
  'city_field',
  'state_field',
  'zipcode_field',
  'optin_date_field',
];

const formatBulkDetailValue = (val) => {
  if (val == null || val === '') return '—';
  if (typeof val === 'number') return String(val);
  const s = String(val).trim();
  return s || '—';
};
// Labels for bulk upload optional fields shown in Upload History expanded row (column mappings + metadata)
const BULK_EXPANDED_FIELD_LABELS = {
  email_field: 'Email column',
  email_md5_field: 'Email MD5 column',
  first_name_field: 'First name column',
  last_name_field: 'Last name column',
  gender_field: 'Gender column',
  dob_field: 'DOB column',
  phone_number_field: 'Phone number column',
  optin_ip_address_field: 'Opt-in IP address column',
  optin_site_field: 'Opt-in site column',
  address_field: 'Address column',
  city_field: 'City column',
  state_field: 'State column',
  zipcode_field: 'Zipcode column',
  optin_date_field: 'Opt-in date column',
  supplier_name: 'Supplier name',
  purchase_date: 'Purchase date',
  quality: 'Quality',
  domains: 'Domains',
  cost: 'Cost',
  agreement: 'Agreement',
};
// Optional bulk mappings: no first-row format validation (parity with validate_bulk.py OPTIONAL_FIELDS).
const BULK_OPTIONAL_FIELDS = new Set(['email_md5', 'first_name', 'last_name', 'gender', 'dob', 'phone_number', 'address', 'city', 'state', 'zipcode']);
// Display names for bulk schema fields (labels in the column mapping UI)
const BULK_SCHEMA_FIELD_LABELS = {
  email: 'Email',
  email_md5: 'Email MD5',
  first_name: 'First name',
  last_name: 'Last name',
  gender: 'Gender',
  dob: 'Date of birth',
  phone_number: 'Phone number',
  optin_ip_address: 'Opt-in IP address',
  optin_site: 'Opt-in site',
  address: 'Address',
  city: 'City',
  state: 'State',
  zipcode: 'Zipcode',
  optin_date: 'Opt-in date',
};
/** Aligned with ``validate_bulk.py`` / ``ipaddress.ip_address`` (Python). */
const isValidClientIpString = (s) => {
  let t = String(s).trim();
  if (!t) return false;
  if (t.includes('%') && t.includes(':')) {
    t = t.split('%')[0].trim();
  }
  if (t.includes(':')) {
    try {
      new URL(`http://[${t}]/`);
      return true;
    } catch {
      return false;
    }
  }
  if (!/^(?:\d{1,3}\.){3}\d{1,3}$/.test(t)) return false;
  return t.split('.').map(Number).every((n) => n >= 0 && n <= 255);
};

// Validation rules for bulk first row: field -> { test: regex or function, message: string }
const BULK_FIRST_ROW_VALIDATORS = {
  email: {
    test: (v) =>
      /^[A-Za-z0-9_%+'-][A-Za-z0-9._%+'-]*@[-A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}$/.test(v),
    message: 'Must be a valid email address.',
  },
  optin_ip_address: {
    test: (v) => isValidClientIpString(v),
    message: 'Must be a valid IPv4 or IPv6 address.',
  },
  optin_site: {
    test: (v) => String(v).trim().length > 0,
    message: 'Must be non-empty.',
  },
  optin_date: {
    test: (v) => !Number.isNaN(Date.parse(String(v).trim())),
    message: 'Must be a valid date/timestamp.',
  },
};
// FastCube has no universe table; universe upload is disabled for this org.
const FASTCUBE_ORG = 'FastCube';

/** 1–40 for validate_universe email_md5_column widget */
const UNIVERSE_COLUMN_OPTIONS = Array.from({ length: 40 }, (_, i) => String(i + 1));
const UNIVERSE_HEADER_PEEK_BYTES = 64 * 1024;
const BULK_PREVIEW_BYTES = 1024 * 1024;

function dropPartialLastLine(text) {
  const lastNl = Math.max(text.lastIndexOf('\n'), text.lastIndexOf('\r'));
  if (lastNl > 0) return text.slice(0, lastNl);
  return text;
}

/** First `{...}` object in a JSON array or document, without reading the rest of a large file. */
function firstJsonObject(text) {
  const start = text.indexOf('{');
  if (start < 0) throw new Error('no object');
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i += 1) {
    const c = text[i];
    if (inString) {
      if (escape) escape = false;
      else if (c === '\\') escape = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"') inString = true;
    else if (c === '{') depth += 1;
    else if (c === '}') {
      depth -= 1;
      if (depth === 0) return JSON.parse(text.slice(start, i + 1));
    }
  }
  throw new Error('truncated');
}

const FileUpload = ({ bulkOnly = false }) => {
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const { can } = usePermissions();
  // Bulk uploads need the bulk.submit role permission in FastCube (approval stays with platform admins).
  const canBulkSubmit = can(PERMISSIONS.BULK_SUBMIT);
  const { startUploadJobPolling, openProcessingReport, reportModalOpen } = useUploadPolling();
  const uploadType = bulkOnly ? 'bulk' : 'universe';
  const [uploading, setUploading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [universeProperties, setUniverseProperties] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Upload history (file_upload for universe; bulk_file_upload on the Bulk page)
  const [uploadHistory, setUploadHistory] = useState([]);
  const [listOffset, setListOffset] = useState(0);
  const [loadingUploadHistory, setLoadingUploadHistory] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  /** null = default (upload time, most recent first) */
  const [userSortDir, setUserSortDir] = useState(null);
  // Bulk page lists only bulk uploads; the Upload page lists only universe uploads.
  const uploadHistoryTypeFilter = bulkOnly ? UPLOAD_HISTORY_TYPE_FILTER.bulk : UPLOAD_HISTORY_TYPE_FILTER.universe;
  const [bulkDetailsRow, setBulkDetailsRow] = useState(null);

  // Comma-separated universe hashes (paste and submit)
  const [universeHashesText, setUniverseHashesText] = useState('');
  // Existing S3 URI (universe input mode s3)
  const [universeS3Path, setUniverseS3Path] = useState('');
  // Universe input mode: 'file' (CSV), 'text' (paste hashes), or 's3' (existing S3 URI). Default File.
  const [universeInputMode, setUniverseInputMode] = useState('file');
  // Universe file mode: file chosen in dropzone, then column/format, then Upload
  const [selectedUniverseFile, setSelectedUniverseFile] = useState(null);
  const [universeFileColumns, setUniverseFileColumns] = useState([]);
  const [universeEmailMd5Column, setUniverseEmailMd5Column] = useState('1');
  const [universeEmailFormat, setUniverseEmailFormat] = useState('md5');
  // Org property -> file column number (1-based) for Universe file/S3 upload; excluded properties are grayed out and omitted.
  const [universePropertyColumns, setUniversePropertyColumns] = useState({});
  const [universePropertyExcluded, setUniversePropertyExcluded] = useState({});
  /** If true, Databricks update_universe drops and recreates the org universe table for this deployment before MERGE. */
  const [universeReplace, setUniverseReplace] = useState(false);
  const [showUniverseReplaceConfirm, setShowUniverseReplaceConfirm] = useState(false);
  const universeReplacePendingRunRef = useRef(null);

  const closeUniverseReplaceConfirm = () => {
    setShowUniverseReplaceConfirm(false);
    universeReplacePendingRunRef.current = null;
  };

  const confirmUniverseReplace = () => {
    const run = universeReplacePendingRunRef.current;
    universeReplacePendingRunRef.current = null;
    setShowUniverseReplaceConfirm(false);
    if (run) void run();
  };

  /** After universe validations pass: confirm drop/reload when checked, else run immediately. */
  const beginUniverseReplaceOrUpload = (performUpload) => {
    if (universeReplace) {
      universeReplacePendingRunRef.current = performUpload;
      setShowUniverseReplaceConfirm(true);
    } else {
      void performUpload();
    }
  };

  // Bulk: selected file and column mapping (after file is chosen)
  const [selectedBulkFile, setSelectedBulkFile] = useState(null);
  const [bulkFileColumns, setBulkFileColumns] = useState([]);
  const [bulkColumnMapping, setBulkColumnMapping] = useState({});
  const [bulkFieldEnabled, setBulkFieldEnabled] = useState({});
  const [bulkMetadata, setBulkMetadata] = useState(
    BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {})
  );
  const [bulkFileParsing, setBulkFileParsing] = useState(false);
  const [bulkFirstRow, setBulkFirstRow] = useState(null);
  /** CSV without header: first-row cells; shown as c0: … | c1: … (labels bold). */
  const [bulkFirstLinePreview, setBulkFirstLinePreview] = useState(null);
  // Bulk (FastCube admin): file upload vs existing S3 object (same mappings as file; column names typed for S3).
  const [bulkInputMode, setBulkInputMode] = useState('file');
  const [bulkS3Path, setBulkS3Path] = useState('');
  const [bulkHasHeader, setBulkHasHeader] = useState(true);
  /** Same as bulkHasHeader; updated synchronously on toggle so file parsing matches the checkbox before React re-renders. */
  const bulkHasHeaderRef = useRef(bulkHasHeader);
  useEffect(() => {
    bulkHasHeaderRef.current = bulkHasHeader;
  }, [bulkHasHeader]);

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        // Load from cookie first, then fetch from API if needed
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);

        if (bulkOnly) {
          // Bulk uploads always target FastCube; don't touch the user's saved organization selection.
          setSelectedOrganization(FASTCUBE_ORG);
          return;
        }
        
        // Load selected organization from cookie, or default to first
        const savedSelected = organizationsService.getSelectedOrganization('name');
        if (savedSelected && orgs.some(o => o.name === savedSelected)) {
          setSelectedOrganization(savedSelected);
          organizationsService.setSelectedOrganization(savedSelected, 'name', orgs);
        } else if (orgs.length > 0) {
          const defaultOrg = orgs[0].name;
          setSelectedOrganization(defaultOrg);
          organizationsService.setSelectedOrganization(defaultOrg, 'name', orgs);
        }
      } catch (error) {
        setErrorMessage('Failed to load organizations. Please refresh the page and try again.');
        setShowErrorDialog(true);
        console.error('Error loading organizations:', error);
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, [canBulkSubmit, bulkOnly]);

  // Reset history paging when org changes.
  useEffect(() => {
    setExpandedHistoryId(null);
    setListOffset(0);
  }, [selectedOrganization, bulkOnly]);

  useEffect(() => {
    const org = organizations.find((o) => o.name === selectedOrganization);
    if (!org?.id || selectedOrganization === FASTCUBE_ORG) {
      setUniverseProperties([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`/api/v2/organizations/${org.id}/properties`)
      .then((res) => {
        if (!cancelled) setUniverseProperties(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setUniverseProperties([]);
      });
    return () => {
      cancelled = true;
    };
  }, [organizations, selectedOrganization]);

  const universeFileHasHeader = useMemo(
    () =>
      universeCsvUsesNamedHeader(universeFileColumns, {
        emailFormat: universeEmailFormat,
        propertyNames: universeProperties.map((p) => p.name),
      }),
    [universeFileColumns, universeEmailFormat, universeProperties]
  );

  const universeFileColumnChoices = useMemo(
    () => getUniverseFileColumnChoices(universeFileColumns, { hasHeader: universeFileHasHeader }),
    [universeFileColumns, universeFileHasHeader]
  );

  const universeFileColumnLabels = useMemo(() => {
    if (!universeFileHasHeader) return {};
    return Object.fromEntries(universeFileColumnChoices.map((opt) => [opt.value, opt.label]));
  }, [universeFileHasHeader, universeFileColumnChoices]);

  // Default property->column mapping: email/md5 is column 1, then properties fill columns 2, 3, …
  // When the local CSV has a header, match Email/md5 and properties to those names.
  useEffect(() => {
    const { emailMd5Column, propertyColumns } = defaultUniverseColumnMappings({
      columnNames: universeFileColumns,
      hasHeader: universeCsvUsesNamedHeader(universeFileColumns, {
        emailFormat: universeEmailFormat,
        propertyNames: universeProperties.map((p) => p.name),
      }),
      properties: universeProperties,
    });
    const excluded = {};
    universeProperties.forEach((p) => {
      excluded[p.name] = false;
    });
    setUniversePropertyColumns(propertyColumns);
    setUniversePropertyExcluded(excluded);
    if (universeFileColumns.length > 0) {
      setUniverseEmailMd5Column(emailMd5Column);
    }
    // emailFormat only changes dropdown labels, not these defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rematch when the file or org properties change
  }, [universeProperties, universeFileColumns]);

  const loadUploadHistoryRef = React.useRef(0);

  const getHistoryRowKey = (row) => `${row.type || 'row'}-${row.id}`;

  const loadUploadHistory = useCallback(async () => {
    if (!currentUser) return;
    const organizationId = organizations.find((o) => o.name === selectedOrganization)?.id;
    if (!bulkOnly && !organizationId) return;
    const generation = loadUploadHistoryRef.current + 1;
    loadUploadHistoryRef.current = generation;
    setLoadingUploadHistory(true);
    try {
      const historyParams = { limit: HISTORY_FETCH_LIMIT };
      // Universe history is every uploader in the selected organization.
      // Bulk history is every uploader in FastCube.
      if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.all) {
        const res = await api.get('/api/v2/file_uploads', {
          params: { ...historyParams, organization_id: organizationId },
        });
        if (loadUploadHistoryRef.current !== generation) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setUploadHistory(rows);
      } else if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.bulk) {
        const res = await api.get('/api/v2/bulk_file_uploads', { params: historyParams });
        if (loadUploadHistoryRef.current !== generation) return;
        setUploadHistory(Array.isArray(res.data) ? res.data : []);
      } else {
        const res = await api.get('/api/v2/file_uploads', {
          params: { ...historyParams, organization_id: organizationId },
        });
        if (loadUploadHistoryRef.current !== generation) return;
        let rows = Array.isArray(res.data) ? res.data : [];
        if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.universe) {
          rows = rows.filter((r) => r.type === 'universe');
        } else if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.fast_id) {
          rows = rows.filter((r) => r.type === 'fast_id');
        }
        setUploadHistory(rows);
      }
    } catch (error) {
      if (loadUploadHistoryRef.current !== generation) return;
      console.error('Upload history error:', error);
      setUploadHistory([]);
    } finally {
      if (loadUploadHistoryRef.current === generation) setLoadingUploadHistory(false);
    }
  }, [currentUser, uploadHistoryTypeFilter, organizations, selectedOrganization, bulkOnly]);

  useEffect(() => {
    loadUploadHistory();
  }, [loadUploadHistory]);

  const sortedUploadHistory = useMemo(() => {
    if (!userSortDir) return uploadHistory;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const withDir = userSortDir === 'asc' ? 1 : -1;
    return [...uploadHistory].sort((a, b) => withDir * collator.compare(a.user_name || '', b.user_name || ''));
  }, [uploadHistory, userSortDir]);

  const toggleUserSort = () => {
    setUserSortDir((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  };

  const pagedUploadHistory = sortedUploadHistory.slice(listOffset, listOffset + LIST_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sortedUploadHistory.length / LIST_PAGE_SIZE) || 1);
  const currentPage = Math.floor(listOffset / LIST_PAGE_SIZE) + 1;

  useEffect(() => {
    if (listOffset > 0 && listOffset >= uploadHistory.length) {
      setListOffset((prev) => Math.max(0, prev - LIST_PAGE_SIZE));
    }
  }, [uploadHistory.length, listOffset]);

  useEffect(() => {
    if (reportModalOpen && location.pathname === (bulkOnly ? '/bulk-upload' : '/file-upload')) {
      loadUploadHistory();
    }
  }, [reportModalOpen, location.pathname, loadUploadHistory, bulkOnly]);

  const handleFileDropzoneReject = useCallback((message) => {
    setErrorMessage(message);
    setShowErrorDialog(true);
  }, []);

  const handleUniverseHashesSubmit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (universeHashesText || '').trim();
    if (!trimmed) {
      setErrorMessage(
        universeEmailFormat === 'email'
          ? 'Please enter at least one email address (comma-separated).'
          : 'Please enter at least one MD5 hash (comma-separated).'
      );
      setShowErrorDialog(true);
      return;
    }
    if (selectedOrganization === FASTCUBE_ORG) {
      setErrorMessage('Universe upload is not available for FastCube (no universe table).');
      setShowErrorDialog(true);
      return;
    }
    const performUpload = async () => {
      setUploading(true);
      try {
        const headers = { 'Content-Type': 'multipart/form-data' };
        const formData = new FormData();
        formData.append('hashes', trimmed);
        formData.append('organization', selectedOrganization);
        formData.append('email_format', universeEmailFormat);
        formData.append('replace', universeReplace ? 'true' : 'false');
        const res = await api.post('/api/v2/upload_universe_hashes', formData, { headers });
        const data = res.data;
        if (data.execution_id) startUploadJobPolling(data.execution_id);
        toast.success('Universe hashes upload started. Check upload history below.');
        setUniverseHashesText('');
        loadUploadHistory();
      } catch (error) {
        console.error('Universe hashes upload error:', error);
        setErrorMessage(error.response?.data?.detail || error.message || 'Failed to upload universe hashes.');
        setShowErrorDialog(true);
      } finally {
        setUploading(false);
      }
    };
    beginUniverseReplaceOrUpload(performUpload);
  };

  const handleUniverseS3Submit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (universeS3Path || '').trim();
    if (!trimmed) {
      setErrorMessage('Please enter an S3 path.');
      setShowErrorDialog(true);
      return;
    }
    if (selectedOrganization === FASTCUBE_ORG) {
      setErrorMessage('Universe upload is not available for FastCube (no universe table).');
      setShowErrorDialog(true);
      return;
    }
    const performUpload = async () => {
      setUploading(true);
      try {
        const headers = { 'Content-Type': 'multipart/form-data' };
        const formData = new FormData();
        formData.append('s3_path', trimmed);
        formData.append('organization', selectedOrganization);
        formData.append('email_md5_column', universeEmailMd5Column);
        formData.append('email_format', universeEmailFormat);
        formData.append('replace', universeReplace ? 'true' : 'false');
        const res = await api.post('/api/v2/upload_universe_s3', formData, { headers });
        const data = res.data;
        setUploading(false);
        if (data.execution_id) startUploadJobPolling(data.execution_id);
        toast.success('Universe upload queued from S3 (copy may run in the background). Check upload history below.');
        loadUploadHistory();
      } catch (error) {
        console.error('Universe S3 path error:', error);
        setErrorMessage(error.response?.data?.detail || error.message || 'Failed to start universe upload from S3.');
        setShowErrorDialog(true);
      } finally {
        setUploading(false);
      }
    };
    beginUniverseReplaceOrUpload(performUpload);
  };

  const handleBulkS3Submit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (selectedOrganization !== FASTCUBE_ORG || !canBulkSubmit) {
      setErrorMessage('Bulk S3 upload needs the bulk upload permission in the FastCube organization.');
      setShowErrorDialog(true);
      return;
    }
    const trimmedPath = (bulkS3Path || '').trim();
    if (!trimmedPath) {
      setErrorMessage('Please enter an S3 path.');
      setShowErrorDialog(true);
      return;
    }
    const requiredBulkMappings = ['email', 'optin_ip_address', 'optin_site', 'optin_date'];
    const missingMappings = requiredBulkMappings.filter((field) => {
      const v = bulkColumnMapping[field];
      return v == null || String(v).trim() === '';
    });
    if (missingMappings.length > 0) {
      const labels = missingMappings.map((f) => BULK_SCHEMA_FIELD_LABELS[f] ?? f);
      setErrorMessage(
        missingMappings.length === 1
          ? `Column mapping required: enter the CSV column name for ${labels[0]}.`
          : `Column mapping required: enter CSV column names for ${labels.join(', ')}.`
      );
      setShowErrorDialog(true);
      return;
    }
    const missingFileInfo = BULK_METADATA_FIELDS.filter((key) => {
      const val = bulkMetadata[key];
      return val == null || String(val).trim() === '';
    });
    if (missingFileInfo.length > 0) {
      const labels = missingFileInfo.map((key) => BULK_METADATA_FIELD_LABELS[key] ?? key);
      setErrorMessage(
        'File Information required: please provide a value for ' +
          (labels.length === 1 ? labels[0] : labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1]) +
          '.'
      );
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const formData = new FormData();
      formData.append('s3_path', trimmedPath);
      formData.append('organization', selectedOrganization);
      formData.append('has_header', bulkHasHeader ? 'true' : 'false');
      BULK_SCHEMA_FIELDS.forEach((field) => {
        const active =
          BULK_ALWAYS_ENABLED_FIELDS.has(field) || bulkFieldEnabled[field] === true;
        const value = active && bulkColumnMapping[field] ? bulkColumnMapping[field] : '';
        formData.append(`${field}_field`, value);
      });
      BULK_METADATA_FIELDS.forEach((key) => {
        const val = bulkMetadata[key];
        formData.append(key, val != null && String(val).trim() !== '' ? String(val).trim() : '');
      });
      const res = await api.post('/api/v2/upload_bulk_s3', formData, { headers });
      const data = res.data;
      setUploading(false);
      if (data.execution_id) startUploadJobPolling(data.execution_id);
      toast.success(data.message || 'Bulk upload queued from S3 (copy may run in the background). Check upload history below.');
      loadUploadHistory();
    } catch (error) {
      console.error('Bulk S3 upload error:', error);
      const detail = error.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg ?? d).join(' ')
            : detail != null
              ? String(detail)
              : '';
      setErrorMessage(detailStr.trim() || error.message || 'Failed to start bulk upload from S3.');
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const clearUniverseFileInput = () => {
    const fileInput = document.getElementById('uploadFile');
    if (fileInput) fileInput.value = '';
    setSelectedUniverseFile(null);
    setUniverseFileColumns([]);
    setUniverseEmailMd5Column('1');
  };

  const handleUniverseFileSelected = async (file) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Universe file size must be 100 GB or less. For larger files, use Universe → S3.');
      setShowErrorDialog(true);
      return;
    }
    let columns = [];
    try {
      const peek = file.slice(0, Math.min(file.size, UNIVERSE_HEADER_PEEK_BYTES));
      const text = await peek.text();
      columns = parseCsvRow(firstCsvLine(text));
    } catch (e) {
      columns = [];
    }
    setUniverseFileColumns(columns);
    setSelectedUniverseFile(file);
  };

  /** Non-excluded org properties with a column number set, as {name: 1-based column index}; '' when none apply. */
  const buildUniversePropertyColumnsPayload = () => {
    const mapping = {};
    universeProperties.forEach((p) => {
      if (universePropertyExcluded[p.name]) return;
      const col = universePropertyColumns[p.name];
      if (col) mapping[p.name] = parseInt(col, 10);
    });
    return Object.keys(mapping).length ? JSON.stringify(mapping) : '';
  };

  const handleUniverseUpload = async () => {
    const file = selectedUniverseFile;
    if (!file) return;
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Universe file size must be 100 GB or less. For larger files, use Universe → S3.');
      setShowErrorDialog(true);
      return;
    }
    const columnConflict = getUniverseDuplicateColumnError({
      emailMd5Column: universeEmailMd5Column,
      properties: universeProperties,
      propertyColumns: universePropertyColumns,
      propertyExcluded: universePropertyExcluded,
      columnLabels: universeFileColumnLabels,
    });
    if (columnConflict) {
      setErrorMessage(columnConflict);
      setShowErrorDialog(true);
      return;
    }
    const performUpload = async () => {
      setUploading(true);
      try {
        const headers = { 'Content-Type': 'multipart/form-data' };
        const { s3_path } = await uploadBrowserFileToS3({
          presignPath: '/api/v2/upload_universe_presign',
          uploadType: 'universe',
          file,
          organization: selectedOrganization,
          headers,
        });
        const formData = new FormData();
        formData.append('s3_path', s3_path);
        formData.append('organization', selectedOrganization);
        formData.append('email_md5_column', universeEmailMd5Column);
        formData.append('email_format', universeEmailFormat);
        formData.append('replace', universeReplace ? 'true' : 'false');
        const propertyColumnsPayload = buildUniversePropertyColumnsPayload();
        if (propertyColumnsPayload) formData.append('property_columns', propertyColumnsPayload);
        const res = await api.post('/api/v2/upload_universe_s3', formData, { headers });
        const data = res.data;
        if (data.execution_id) startUploadJobPolling(data.execution_id);
        toast.success('Universe upload started. Check upload history below.');
        clearUniverseFileInput();
        loadUploadHistory();
      } catch (error) {
        console.error('Universe upload error:', error);
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        const detailStr =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d?.msg ?? d).join(' ')
              : detail != null
                ? String(detail)
                : '';
        let msg = detailStr.trim() || error.message || 'Failed to upload universe file.';
        const s3Hint = formatS3DirectUploadError(error);
        if (s3Hint) {
          msg = s3Hint;
        } else if (status === 413) {
          msg =
            'The upload was rejected (HTTP 413). A load balancer, CDN, or reverse proxy in front of the API often enforces a limit below 100 GB, even for smaller files. Use Universe input → S3 and enter s3://bucket/path to your CSV instead.';
        } else if (!error.response) {
          if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
            msg =
              'The upload timed out or was cancelled. For large files, use Universe input → S3 and enter s3://bucket/path to your CSV instead.';
          } else if (error.message === 'Network Error') {
            msg =
              'Network error while uploading. For large files, use Universe input → S3 and enter s3://bucket/path to your CSV instead.';
          }
        }
        setErrorMessage(msg);
        setShowErrorDialog(true);
      } finally {
        setUploading(false);
      }
    };
    beginUniverseReplaceOrUpload(performUpload);
  };

  const handleFileUpload = (file) => {
    if (uploadType === 'universe') {
      handleUniverseFileSelected(file);
    } else if (uploadType === 'bulk') {
      handleBulkFileSelected(file);
    }
  };

  /** Parse Bulk file to get column names; set state for mapping UI. */
  const handleBulkFileSelected = async (file) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    const name = (file.name || '').toLowerCase();
    const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
    if (!BULK_ALLOWED_EXTENSIONS.includes(ext)) {
      setErrorMessage('Bulk file must be .json or .csv.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Bulk file size must be 100 GB or less. Split the file or reduce size before uploading.');
      setShowErrorDialog(true);
      return;
    }
    setBulkFileParsing(true);
    setSelectedBulkFile(null);
    setBulkFileColumns([]);
    setBulkColumnMapping({});
    setBulkFirstLinePreview(null);
    try {
      let columns = [];
      let firstRow = null;
      let firstLinePreview = null;
      const truncated = file.size > BULK_PREVIEW_BYTES;
      const raw = await file.slice(0, Math.min(file.size, BULK_PREVIEW_BYTES)).text();
      const text = truncated ? dropPartialLastLine(raw) : raw;
      if (ext === '.csv') {
        const hasHeader = bulkHasHeaderRef.current;
        const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const firstDataRowIndex = hasHeader ? 1 : 0;
        const previewLine = lines[firstDataRowIndex];
        if (!hasHeader && previewLine !== undefined) {
          firstLinePreview = previewLine
            .split(',')
            .map((s) => s.trim().replace(/^"|"$/g, ''));
        }
        if (hasHeader) {
          const headerLine = lines[0] || '';
          columns = headerLine.split(',').map((h) => h.trim()).filter(Boolean);
        } else {
          const valueLine = lines[0] || '';
          const values = valueLine.split(',');
          columns = values.map((_, i) => `_c${i}`);
        }
        if (lines.length > firstDataRowIndex && columns.length > 0) {
          const valueLine = lines[firstDataRowIndex];
          const rawValues = valueLine.split(',').map((s) => s.trim().replace(/^"|"$/g, ''));
          firstRow = {};
          columns.forEach((col, i) => {
            firstRow[col] = rawValues[i] !== undefined ? String(rawValues[i]).trim() : '';
          });
        }
      } else {
        const first = truncated ? firstJsonObject(raw) : (() => {
          const data = JSON.parse(raw);
          return Array.isArray(data) ? data[0] : data;
        })();
        if (first && typeof first === 'object' && !Array.isArray(first)) {
          columns = Object.keys(first);
          firstRow = {};
          columns.forEach((col) => {
            const v = first[col];
            firstRow[col] = v != null ? String(v).trim() : '';
          });
        }
      }
      setBulkFileColumns(columns);
      setBulkFirstRow(firstRow);
      setBulkFirstLinePreview(firstLinePreview);
      const defaultMapping = {};
      BULK_SCHEMA_FIELDS.forEach((field) => {
        defaultMapping[field] =
          BULK_ALWAYS_ENABLED_FIELDS.has(field) && columns.includes(field) ? field : '';
      });
      setBulkColumnMapping(defaultMapping);
      setBulkFieldEnabled(defaultBulkFieldEnabled());
      setSelectedBulkFile(file);
    } catch (e) {
      setErrorMessage('Could not read file columns. Check that the file is valid CSV (with header) or JSON.');
      setShowErrorDialog(true);
    } finally {
      setBulkFileParsing(false);
    }
  };

  /** Validate first row by current mapping; returns { field: errorMessage }. Only validates enabled, mapped fields. */
  const getBulkValidationErrors = () => {
    if (!bulkFirstRow || !bulkColumnMapping) return {};
    const errors = {};
    BULK_SCHEMA_FIELDS.forEach((field) => {
      if (!BULK_ALWAYS_ENABLED_FIELDS.has(field) && bulkFieldEnabled[field] !== true) return;
      const col = bulkColumnMapping[field];
      if (!col) return;
      if (BULK_OPTIONAL_FIELDS.has(field)) return;
      const value = bulkFirstRow[col];
      const str = value != null ? String(value).trim() : '';
      const rule = BULK_FIRST_ROW_VALIDATORS[field];
      if (str === '') {
        errors[field] = rule ? rule.message : 'Required.';
        return;
      }
      if (rule && !rule.test(str)) errors[field] = rule.message;
    });
    return errors;
  };

  const handleBulkUpload = async (file, columnMapping = {}) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    const requiredBulkMappings = ['email', 'optin_ip_address', 'optin_site', 'optin_date'];
    const missingMappings = requiredBulkMappings.filter((field) => {
      const v = columnMapping[field];
      return v == null || String(v).trim() === '';
    });
    if (missingMappings.length > 0) {
      const labels = missingMappings.map((f) => BULK_SCHEMA_FIELD_LABELS[f] ?? f);
      setErrorMessage(
        missingMappings.length === 1
          ? `Column mapping required: select a file column for ${labels[0]} instead of leaving it on "Select column".`
          : `Column mapping required: select file columns for ${labels.join(', ')} instead of leaving any on "Select column".`
      );
      setShowErrorDialog(true);
      return;
    }
    const validationErrors = getBulkValidationErrors();
    if (Object.keys(validationErrors).length > 0) {
      const msg = 'First row validation failed: ' + Object.entries(validationErrors).map(([f, m]) => `${f}: ${m}`).join('; ');
      setErrorMessage(msg);
      setShowErrorDialog(true);
      return;
    }
    // File Information: each field must have a non-space value
    const missingFileInfo = BULK_METADATA_FIELDS.filter((key) => {
      const val = bulkMetadata[key];
      return val == null || String(val).trim() === '';
    });
    if (missingFileInfo.length > 0) {
      const labels = missingFileInfo.map((key) => BULK_METADATA_FIELD_LABELS[key] ?? key);
      setErrorMessage(
        'File Information required: please provide a value for ' +
          (labels.length === 1 ? labels[0] : labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1]) +
          '.'
      );
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const { s3_path } = await uploadBrowserFileToS3({
        presignPath: '/api/v2/upload_bulk_presign',
        uploadType: 'bulk',
        file,
        organization: selectedOrganization,
        headers,
      });
      const formData = new FormData();
      formData.append('s3_path', s3_path);
      formData.append('organization', selectedOrganization);
      formData.append('has_header', bulkHasHeader ? 'true' : 'false');
      // All mapping fields: send selected file column name for each schema field (empty if not mapped)
      BULK_SCHEMA_FIELDS.forEach((field) => {
        const active =
          BULK_ALWAYS_ENABLED_FIELDS.has(field) || bulkFieldEnabled[field] === true;
        const value = active && columnMapping[field] ? columnMapping[field] : '';
        formData.append(`${field}_field`, value);
      });
      // All metadata fields: send value or empty string
      BULK_METADATA_FIELDS.forEach((key) => {
        const val = bulkMetadata[key];
        formData.append(key, val != null && String(val).trim() !== '' ? String(val).trim() : '');
      });
      const res = await api.post('/api/v2/upload_bulk_s3', formData, { headers });
      const data = res.data;
      if (data.execution_id) startUploadJobPolling(data.execution_id);
      toast.success(data.message || 'Bulk file uploaded successfully.');
      loadUploadHistory();
      const fileInput = document.getElementById('uploadFile');
      if (fileInput) fileInput.value = '';
      setSelectedBulkFile(null);
      setBulkFileColumns([]);
      setBulkColumnMapping({});
      setBulkFieldEnabled({});
      setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
      setBulkFirstRow(null);
      setBulkFirstLinePreview(null);
    } catch (error) {
      console.error('Bulk upload error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg ?? d).join(' ')
            : detail != null
              ? String(detail)
              : '';
      let msg = detailStr.trim() || error.message || 'Failed to upload bulk file.';
      const s3Hint = formatS3DirectUploadError(error);
      if (s3Hint) {
        msg = s3Hint;
      } else if (status === 413) {
        msg =
          'The upload was rejected (HTTP 413). A load balancer, CDN, or reverse proxy in front of the API often enforces a limit below 100 GB, even when your file is under 100 GB. Use Bulk input → S3 with s3://bucket/path, split the file, or ask your team to raise the upload size limit in front of the API.';
      } else if (!error.response) {
        if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
          msg =
            'The upload timed out or was cancelled. Try a smaller file or split the data.';
        } else if (error.message === 'Network Error') {
          msg = 'Network error while uploading. Check your connection and try again.';
        }
      }
      setErrorMessage(msg);
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pb-6 space-y-6">
      <PageHeaderActions>
        {bulkOnly ? null : organizationsLoading || organizations.length === 0 ? (
          <FunctionalSelect
            label="Organization"
            value=""
            onValueChange={() => {}}
            placeholder={organizationsLoading ? 'Loading...' : 'No organizations available'}
            className={HEADER_SELECT_CLASS}
            disabled
          />
        ) : (
          <FunctionalSelect
            label="Organization"
            value={organizationsService.getSelectedOptionValue(organizations, selectedOrganization, 'name')}
            onValueChange={(value) => {
              const parsed = organizationsService.parseOrganizationOptionValue(value);
              const name = parsed ? parsed.name : value;
              if (parsed) {
                organizationsService.setSelectedOrganization(name, 'name', [{ name: parsed.name, id: parsed.id }]);
              } else {
                organizationsService.setSelectedOrganization(name, 'name', organizations);
              }
              setSelectedOrganization(name);
              setSelectedUniverseFile(null);
            }}
            placeholder="Select organization"
            className={HEADER_SELECT_CLASS}
          >
            {organizations.map((org) => (
              <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                {org.name}
              </SelectItem>
            ))}
          </FunctionalSelect>
        )}
      </PageHeaderActions>
      <Card>
        <CardContent className="space-y-4 pt-6">
          {!bulkOnly && !organizationsLoading && organizations.length === 0 ? (
            <p className="text-sm text-amber-600">
              No organizations available. Please contact an administrator to be added to an organization.
            </p>
          ) : null}

          {!bulkOnly && selectedOrganization === FASTCUBE_ORG && canBulkSubmit && (
            <p className="text-sm text-gray-500">
              Bulk uploads have moved to the{' '}
              <Link to="/bulk-upload" className="text-[#0062FB] hover:underline">
                Bulk Upload
              </Link>{' '}
              page.
            </p>
          )}

          {!bulkOnly && selectedOrganization === FASTCUBE_ORG && (
            <p className="text-sm text-gray-500">
              FastID uploads have moved to the{' '}
              <Link to="/fastid-report" className="text-[#0062FB] hover:underline">
                FastID Reports
              </Link>{' '}
              page.
            </p>
          )}

          {uploadType === 'bulk' && (
            <div className="space-y-2">
              <Label>Bulk input</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bulkInputMode"
                    value="file"
                    checked={bulkInputMode === 'file'}
                    onChange={() => {
                      setBulkInputMode('file');
                      setBulkS3Path('');
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">File</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bulkInputMode"
                    value="s3"
                    checked={bulkInputMode === 's3'}
                    onChange={() => {
                      setBulkInputMode('s3');
                      setSelectedBulkFile(null);
                      setBulkFileColumns([]);
                      setBulkFirstRow(null);
                      setBulkFirstLinePreview(null);
                      setBulkColumnMapping(BULK_SCHEMA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                      setBulkFieldEnabled(defaultBulkFieldEnabled());
                      const fileInput = document.getElementById('uploadFile');
                      if (fileInput) fileInput.value = '';
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">S3</span>
                </label>
              </div>
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="bulkHasHeader"
                  checked={bulkHasHeader}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    bulkHasHeaderRef.current = checked;
                    setBulkHasHeader(checked);
                    if (bulkInputMode === 'file') {
                      setSelectedBulkFile(null);
                      setBulkFileColumns([]);
                      setBulkColumnMapping({});
                      setBulkFieldEnabled({});
                      setBulkFirstRow(null);
                      setBulkFirstLinePreview(null);
                      const fileInput = document.getElementById('uploadFile');
                      if (fileInput) fileInput.value = '';
                    }
                  }}
                  disabled={uploading}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="bulkHasHeader" className="cursor-pointer">
                  Has Header
                </Label>
              </div>
            </div>
          )}

          {uploadType === 'universe' && selectedOrganization !== FASTCUBE_ORG && (
            <div className="space-y-2">
              <Label>Universe input</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="universeInputMode"
                    value="file"
                    checked={universeInputMode === 'file'}
                    onChange={() => {
                      setUniverseInputMode('file');
                      setSelectedUniverseFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">File</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="universeInputMode"
                    value="text"
                    checked={universeInputMode === 'text'}
                    onChange={() => {
                      setUniverseInputMode('text');
                      setSelectedUniverseFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">Text</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="universeInputMode"
                    value="s3"
                    checked={universeInputMode === 's3'}
                    onChange={() => {
                      setUniverseInputMode('s3');
                      setSelectedUniverseFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">S3</span>
                </label>
              </div>
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="universeReplace"
                  checked={universeReplace}
                  onChange={(e) => setUniverseReplace(e.target.checked)}
                  disabled={uploading}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="universeReplace" className="cursor-pointer">
                  Replace universe table (drop and reload)
                </Label>
              </div>
            </div>
          )}

          {((uploadType === 'bulk' && bulkInputMode === 'file') ||
            (uploadType === 'universe' && universeInputMode === 'file' && selectedOrganization !== FASTCUBE_ORG)) && (
            <div className="space-y-2">
              <FileDropzone
                inputId="uploadFile"
                label={uploadType === 'universe' ? 'Select Universe CSV' : 'Select Bulk File'}
                accept={uploadType === 'bulk' ? '.csv,.json' : '.csv'}
                maxSize={MAX_UPLOAD_FILE_BYTES}
                tooLargeHint={
                  uploadType === 'universe'
                    ? 'Use Universe input → S3 and enter s3://bucket/path to your CSV instead.'
                    : 'Split the file or reduce size before uploading.'
                }
                onReject={handleFileDropzoneReject}
                disabled={
                  uploading ||
                  !selectedOrganization ||
                  organizationsLoading ||
                  organizations.length === 0 ||
                  (uploadType === 'universe' && selectedOrganization === FASTCUBE_ORG)
                }
                onFileSelect={handleFileUpload}
              />
              {uploading && (
                <p className="text-sm text-blue-600">Uploading file...</p>
              )}
              {uploadType === 'universe' && selectedOrganization === FASTCUBE_ORG && (
                <p className="text-sm text-amber-600">
                  Universe upload is not available for FastCube (no universe table).
                </p>
              )}
              {!organizationsLoading && organizations.length === 0 && (
                <p className="text-sm text-amber-600">
                  File upload is disabled because you are not associated with any organizations.
                </p>
              )}
              {!organizationsLoading &&
                organizations.length > 0 &&
                !(uploadType === 'universe' && selectedOrganization === FASTCUBE_ORG) && (
                <p className="text-sm text-gray-500">
                  {uploadType === 'universe'
                    ? 'Select a universe file to upload (CSV or JSON).' +
                      (universeProperties.length
                        ? ' Matching header columns populate custom properties automatically, or set each property’s column below (uncheck "exclude" to include it).'
                        : '')
                    : 'Select a bulk file to upload (CSV or JSON).'}
                </p>
              )}
              {uploadType === 'bulk' && (
                <>
                  {bulkFileParsing && <p className="text-sm text-blue-600">Reading file...</p>}
                  {selectedBulkFile && (
                    <p className="text-sm text-gray-600">
                      Selected: <span className="font-medium">{selectedBulkFile.name}</span>
                    </p>
                  )}
                </>
              )}
              {uploadType === 'universe' && universeInputMode === 'file' && selectedUniverseFile && (
                <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
                  <p className="text-sm text-gray-600">
                    Selected: <span className="font-medium">{selectedUniverseFile.name}</span>
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                                            <FunctionalSelect
                        label="Email/md5 column"
                        id="universeEmailMd5Column"
                        value={universeEmailMd5Column}
                        onValueChange={setUniverseEmailMd5Column}
                        placeholder="Select"
                        className="w-full"
                        disabled={uploading}
                      >
                        {universeFileColumnChoices.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </FunctionalSelect>
                    </div>
                    <div className="space-y-2">
                                            <FunctionalSelect
                        label="Email format"
                        id="universeEmailFormat"
                        value={universeEmailFormat}
                        onValueChange={setUniverseEmailFormat}
                        placeholder="Select"
                        className="w-full"
                        disabled={uploading}
                      >
                        <SelectItem value="md5">md5</SelectItem>
                        <SelectItem value="email">email</SelectItem>
                      </FunctionalSelect>
                    </div>
                  </div>
                  {universeProperties.length > 0 && (
                    <div className="space-y-2 border-t border-gray-100 pt-4">
                      <p className="text-sm font-medium text-gray-700">Organization properties</p>
                      <div className="grid gap-x-3 gap-y-2 grid-cols-[auto_1fr] items-center">
                        {universeProperties.map((p) => {
                          const excluded = !!universePropertyExcluded[p.name];
                          const included = !excluded;
                          return (
                            <React.Fragment key={p.name}>
                              <input
                                type="checkbox"
                                id={`universe-prop-include-${p.name}`}
                                checked={included}
                                onChange={(e) =>
                                  setUniversePropertyExcluded((prev) => ({
                                    ...prev,
                                    [p.name]: !e.target.checked,
                                  }))
                                }
                                disabled={uploading}
                                className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300 rounded"
                              />
                              <FunctionalSelect
                                id={`universe-prop-col-${p.name}`}
                                label={`${p.display_name || p.name} column`}
                                value={universePropertyColumns[p.name] || ''}
                                onValueChange={(value) =>
                                  setUniversePropertyColumns((prev) => ({ ...prev, [p.name]: value }))
                                }
                                placeholder="Select"
                                className={`w-full ${excluded ? 'opacity-60' : ''}`}
                                disabled={uploading || excluded}
                              >
                                {universeFileColumnChoices.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </FunctionalSelect>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleUniverseUpload}
                      disabled={
                        uploading ||
                        !selectedOrganization ||
                        organizationsLoading ||
                        organizations.length === 0 ||
                        selectedOrganization === FASTCUBE_ORG
                      }
                      className="inline-flex h-10 w-10 items-center justify-center border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Upload"
                      title="Upload"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <UploadIcon className="h-4 w-4" aria-hidden />}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        clearUniverseFileInput();
                      }}
                      disabled={uploading}
                      className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {uploadType === 'bulk' &&
            ((bulkInputMode === 'file' && selectedBulkFile && bulkFileColumns.length > 0) ||
              bulkInputMode === 's3') && (
            <div className="space-y-4 border-t pt-4">
              {bulkInputMode === 's3' && (
                <div className="space-y-2">
                  <Label htmlFor="bulkS3Path">S3 Path</Label>
                  <input
                    id="bulkS3Path"
                    type="text"
                    value={bulkS3Path}
                    onChange={(e) => setBulkS3Path(e.target.value)}
                    placeholder="s3://your-bucket/path/to/file.csv"
                    disabled={
                      uploading ||
                      !selectedOrganization ||
                      organizationsLoading ||
                      organizations.length === 0
                    }
                    className="w-full px-3 py-2 border border-hairline rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                    autoComplete="off"
                  />
                  <p className="text-sm text-gray-500">
                    Use a CSV or JSON file already in S3 (same job as file upload; copy into the uploads bucket may run in the background). Enter column names exactly as in the file header.
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Label className="text-gray-700">File Information</Label>
                <div className="grid gap-x-4 gap-y-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {BULK_METADATA_FIELDS.map((key) => (
                    <div key={key} className="space-y-1">
                      <Label htmlFor={`bulk-meta-${key}`} className="text-sm text-gray-600">
                        {BULK_METADATA_FIELD_LABELS[key] ?? key}
                      </Label>
                      <input
                        id={`bulk-meta-${key}`}
                        type="text"
                        value={bulkMetadata[key] ?? ''}
                        onChange={(e) => setBulkMetadata((prev) => ({ ...prev, [key]: e.target.value }))}
                        disabled={uploading}
                        className="w-full px-3 py-2 border border-hairline rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3 pt-4 border-t">
                <Label>Map file columns to schema</Label>
                <p className="text-sm text-gray-500">
                  {bulkInputMode === 's3'
                    ? 'Check each field you want to map, then type the exact CSV/JSON column name. Email, Opt-in IP address, and Opt-in date are required and cannot be unchecked.'
                    : 'Check each field you want to map, then choose the file column. Email, Opt-in IP address, and Opt-in date are required and cannot be unchecked. First row is validated below.'}
                </p>
                {bulkInputMode === 'file' &&
                  !bulkHasHeader &&
                  bulkFirstLinePreview != null &&
                  bulkFirstLinePreview.length > 0 && (
                    <p className="text-sm text-gray-800 font-mono break-all whitespace-pre-wrap">
                      {bulkFirstLinePreview.map((cell, i) => (
                        <React.Fragment key={i}>
                          {i > 0 ? ' | ' : null}
                          <span className="font-bold">c{i}:</span>
                          {cell ? ` ${cell}` : ''}
                        </React.Fragment>
                      ))}
                    </p>
                  )}
                <div className="grid gap-x-4 gap-y-3 grid-cols-[auto_minmax(0,max-content)_minmax(0,1fr)_minmax(0,1.5fr)] items-center">
                  {BULK_SCHEMA_FIELDS.map((field) => {
                    const fieldError = getBulkValidationErrors()[field];
                    const locked = BULK_ALWAYS_ENABLED_FIELDS.has(field);
                    const enabled = locked || bulkFieldEnabled[field] === true;
                    return (
                      <React.Fragment key={field}>
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            id={`bulk-enable-${field}`}
                            checked={enabled}
                            onChange={(e) => {
                              if (BULK_ALWAYS_ENABLED_FIELDS.has(field)) return;
                              const checked = e.target.checked;
                              setBulkFieldEnabled((prev) => ({ ...prev, [field]: checked }));
                              if (!checked) setBulkColumnMapping((prev) => ({ ...prev, [field]: '' }));
                            }}
                            disabled={uploading || locked}
                            className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300 rounded"
                          />
                        </div>
                        <Label
                          htmlFor={`bulk-enable-${field}`}
                          className={`text-sm ${locked ? 'cursor-default' : 'cursor-pointer'} ${enabled ? 'text-gray-700' : 'text-gray-400'}`}
                        >
                          <span className="font-bold">{BULK_SCHEMA_FIELD_LABELS[field] ?? field}</span>
                        </Label>
                        {bulkInputMode === 's3' ? (
                          <input
                            id={`bulk-map-s3-${field}`}
                            type="text"
                            value={bulkColumnMapping[field] ?? ''}
                            onChange={(e) =>
                              setBulkColumnMapping((prev) => ({ ...prev, [field]: e.target.value }))
                            }
                            placeholder="Column name in file"
                            disabled={uploading || !enabled}
                            className={`w-full px-3 py-2 border border-hairline rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed ${enabled ? '' : 'opacity-60'}`}
                            autoComplete="off"
                          />
                        ) : (
                          <FunctionalSelect
                            id={`bulk-map-${field}`}
                            value={bulkColumnMapping[field] ?? ''}
                            onValueChange={(value) => {
                              setBulkColumnMapping((prev) => ({ ...prev, [field]: value }));
                            }}
                            placeholder="Select column"
                            className={`w-full ${enabled ? '' : 'opacity-60'}`}
                            disabled={uploading || !enabled}
                          >
                            {[...bulkFileColumns].sort((a, b) => a.localeCompare(b)).map((col) => (
                              <SelectItem key={col} value={col}>
                                {col}
                              </SelectItem>
                            ))}
                          </FunctionalSelect>
                        )}
                        <div className="min-h-[1.25rem]">
                          {fieldError && (
                            <p className="text-xs text-red-600">{fieldError}</p>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                {bulkInputMode === 'file' ? (
                  <button
                    type="button"
                    onClick={() => handleBulkUpload(selectedBulkFile, bulkColumnMapping)}
                    disabled={uploading || Object.keys(getBulkValidationErrors()).length > 0}
                    className="inline-flex h-10 w-10 items-center justify-center border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Upload"
                    title="Upload"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <UploadIcon className="h-4 w-4" aria-hidden />}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleBulkS3Submit}
                    disabled={
                      uploading ||
                      !(bulkS3Path || '').trim() ||
                      !selectedOrganization ||
                      organizationsLoading ||
                      organizations.length === 0 ||
                      ['email', 'optin_ip_address', 'optin_date'].some(
                        (f) => !String(bulkColumnMapping[f] || '').trim()
                      ) ||
                      BULK_METADATA_FIELDS.some((key) => !String(bulkMetadata[key] || '').trim())
                    }
                    className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploading ? 'Submitting…' : 'Submit'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (bulkInputMode === 's3') {
                      setBulkS3Path('');
                      setBulkColumnMapping(BULK_SCHEMA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                      setBulkFieldEnabled(defaultBulkFieldEnabled());
                      setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                      return;
                    }
                    setSelectedBulkFile(null);
                    setBulkFileColumns([]);
                    setBulkColumnMapping({});
                    setBulkFieldEnabled({});
                    setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                    setBulkFirstRow(null);
                    setBulkFirstLinePreview(null);
                    const fileInput = document.getElementById('uploadFile');
                    if (fileInput) fileInput.value = '';
                  }}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {uploadType === 'universe' && universeInputMode === 's3' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="universeS3Path">S3 Path</Label>
                <input
                  id="universeS3Path"
                  type="text"
                  value={universeS3Path}
                  onChange={(e) => setUniverseS3Path(e.target.value)}
                  placeholder="s3://<bucket>/<prefix>"
                  disabled={uploading || !selectedOrganization || organizationsLoading || organizations.length === 0 || selectedOrganization === FASTCUBE_ORG}
                  className="w-full px-3 py-2 border border-hairline rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                                    <FunctionalSelect
                    label="Email/md5 column"
                    id="universeS3EmailMd5Column"
                    value={universeEmailMd5Column}
                    onValueChange={setUniverseEmailMd5Column}
                    placeholder="Select"
                    className="w-full"
                    disabled={uploading}
                  >
                    {UNIVERSE_COLUMN_OPTIONS.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </FunctionalSelect>
                </div>
                <div className="space-y-2">
                                    <FunctionalSelect
                    label="Email format"
                    id="universeS3EmailFormat"
                    value={universeEmailFormat}
                    onValueChange={setUniverseEmailFormat}
                    placeholder="Select"
                    className="w-full"
                    disabled={uploading}
                  >
                    <SelectItem value="md5">md5</SelectItem>
                    <SelectItem value="email">email</SelectItem>
                  </FunctionalSelect>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Use an existing CSV in S3 that Databricks can read (recommended for files over 100 GB). Column and format match file upload and are passed to validation in Databricks.
              </p>
              <button
                type="button"
                onClick={handleUniverseS3Submit}
                disabled={uploading || !(universeS3Path || '').trim() || !selectedOrganization || organizationsLoading || organizations.length === 0 || selectedOrganization === FASTCUBE_ORG}
                className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit
              </button>
            </div>
          )}

          {uploadType === 'universe' && universeInputMode === 'text' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Text is uploaded as one value per line (single column). Same format option as file and S3 upload (Databricks validate_universe).
              </p>
              <div className="space-y-2">
                <Label htmlFor="universeHashes">
                  {universeEmailFormat === 'email'
                    ? 'Paste comma-separated email addresses'
                    : 'Paste comma-separated MD5 hashes'}
                </Label>
                <textarea
                  id="universeHashes"
                  value={universeHashesText}
                  onChange={(e) => setUniverseHashesText(e.target.value)}
                  placeholder={
                    universeEmailFormat === 'email'
                      ? 'e.g. user@example.com, other@yahoo.com'
                      : 'e.g. abc123..., def456... (32 hex chars each)'
                  }
                  rows={5}
                  disabled={uploading || !selectedOrganization || organizationsLoading || organizations.length === 0 || selectedOrganization === FASTCUBE_ORG}
                  className="w-full px-3 py-2 border border-hairline rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                />
              </div>
              <div className="space-y-2 max-w-sm">
                                <FunctionalSelect
                  label="Email format"
                  id="universeTextEmailFormat"
                  value={universeEmailFormat}
                  onValueChange={setUniverseEmailFormat}
                  placeholder="Select"
                  className="w-full"
                  disabled={uploading}
                >
                  <SelectItem value="md5">md5</SelectItem>
                  <SelectItem value="email">email</SelectItem>
                </FunctionalSelect>
              </div>
              <button
                type="button"
                onClick={handleUniverseHashesSubmit}
                disabled={uploading || !(universeHashesText || '').trim() || !selectedOrganization || organizationsLoading || organizations.length === 0 || selectedOrganization === FASTCUBE_ORG}
                className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit
              </button>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Upload History: universe rows for the selected org, or FastCube bulk rows, from any uploader */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <CardTitle className="mb-0">Upload History</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingUploadHistory || (!bulkOnly && organizationsLoading) ? (
            <p className="text-sm text-gray-500">Loading upload history...</p>
          ) : uploadHistory.length === 0 ? (
            <p className="text-sm text-gray-500">No uploads yet.</p>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-md">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border-b w-9" aria-label="Expand" />
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Upload time</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                      <button
                        type="button"
                        onClick={toggleUserSort}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                      >
                        User
                        {userSortDir === 'asc' ? (
                          <ChevronUp size={14} />
                        ) : userSortDir === 'desc' ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">File name</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Organization</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b min-w-[11rem]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUploadHistory.map((row) => {
                    const rowKey = getHistoryRowKey(row);
                    const showProcessingReportButton =
                      (row.processing_status === 'validated' ||
                        row.processing_status === 'complete' ||
                        row.processing_status === 'error') &&
                      Boolean(row.execution_id);
                    const showFastIdReportButton =
                      row.processing_status === 'complete' && Boolean(row.execution_id);
                    return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedHistoryId((prev) => (prev === rowKey ? null : rowKey))}
                      >
                        <td className="px-2 py-2 text-gray-500" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setExpandedHistoryId((prev) => (prev === rowKey ? null : rowKey))}
                            className="p-0.5 rounded hover:bg-gray-200"
                            aria-expanded={expandedHistoryId === rowKey}
                          >
                            {expandedHistoryId === rowKey ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {row.upload_time ? new Date(row.upload_time).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{row.user_name || '—'}</td>
                        <td className="px-3 py-2 text-gray-800">{row.file_name || '—'}</td>
                        <td className="px-3 py-2 text-gray-800">{row.organization_name || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            row.processing_status === 'started' || row.processing_status === 'validated'
                              ? 'bg-green-100 text-green-800' :
                            row.processing_status === 'complete' ? 'bg-blue-100 text-blue-800' :
                            row.processing_status === 'error' ? 'bg-red-100 text-red-800' :
                            row.processing_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {row.processing_status || '—'}
                          </span>
                        </td>
                        <td
                          className="px-3 py-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.type === 'bulk' ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setBulkDetailsRow(row)}
                               className={ROW_ICON_CLASS} aria-label="Details" title="Details">
<Info className="h-4 w-4" aria-hidden />
</button>
                              {showProcessingReportButton && (
                                <button
                                  type="button"
                                  onClick={() => openProcessingReport(row.execution_id)}
                                 className={ROW_ICON_CLASS} aria-label="Report" title="Report">
<FileText className="h-4 w-4" aria-hidden />
</button>
                              )}
                            </div>
                          ) : row.type === 'universe' ? (
                            showProcessingReportButton ? (
                              <button
                                type="button"
                                onClick={() => openProcessingReport(row.execution_id)}
                               className={ROW_ICON_CLASS} aria-label="Report" title="Report">
<FileText className="h-4 w-4" aria-hidden />
</button>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )
                          ) : row.type === 'fast_id' ? (
                            showFastIdReportButton ? (
                              <Link
                                to={`/fastid-report/${row.execution_id}`}
                                className={ROW_ICON_CLASS}
                                aria-label="Report"
                                title="Report"
                              >
                                <FileText className="h-4 w-4" aria-hidden />
                              </Link>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                      </tr>
                      {expandedHistoryId === rowKey && (
                        <tr key={`${rowKey}-expanded`} className="border-b border-gray-100 bg-gray-50">
                          <td colSpan={7} className="px-3 py-2">
                            <div className="text-xs text-gray-600 space-y-1">
                              {selectedOrganization === FASTCUBE_ORG && (
                                <div>
                                  <span className="font-medium text-gray-700">S3 URL:</span>{' '}
                                  <code className="break-all bg-gray-100 px-2 py-1 rounded" title={row.s3_url}>
                                    {row.s3_url || '—'}
                                  </code>
                                </div>
                              )}
                              {row.execution_id && (
                                <div>
                                  <span className="font-medium text-gray-700">Execution ID:</span>{' '}
                                  <code className="break-all bg-gray-100 px-2 py-1 rounded" title={row.execution_id}>
                                    {row.execution_id}
                                  </code>
                                </div>
                              )}
                              {row.upload_id != null && (
                                <div>
                                  <span className="font-medium text-gray-700">Upload ID:</span>{' '}
                                  <code className="break-all bg-gray-100 px-2 py-1 rounded" title={String(row.upload_id)}>
                                    {row.upload_id}
                                  </code>
                                </div>
                              )}
                              {row.file_md5 ? (
                                <div>
                                  <span className="font-medium text-gray-700">File MD5:</span>{' '}
                                  <code
                                    className="break-all bg-gray-100 px-2 py-1 rounded"
                                    title={row.file_md5}
                                  >
                                    {row.file_md5}
                                  </code>
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 pt-3 text-sm text-gray-600">
              <span>
                {uploadHistory.length.toLocaleString()} upload
                {uploadHistory.length === 1 ? '' : 's'}
                {` · page ${currentPage} of ${totalPages}`}
              </span>
              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                disabled={loadingUploadHistory}
                onPageChange={(page) => {
                  setListOffset((page - 1) * LIST_PAGE_SIZE);
                  setExpandedHistoryId(null);
                }}
              />
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {bulkDetailsRow && (
      <Modal
        isOpen
        onClose={() => setBulkDetailsRow(null)}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
            <CardHeader className="pr-10">
              <CardTitle>Bulk upload details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-800 space-y-5">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">File information</h4>
                  <div className="space-y-1.5">
                    {BULK_METADATA_FIELDS.map((key) => (
                      <div key={key}>
                        <span className="font-medium text-gray-800">{BULK_METADATA_FIELD_LABELS[key]}:</span>{' '}
                        <span>{formatBulkDetailValue(bulkDetailsRow[key])}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Column mapping</h4>
                  <div className="space-y-1.5">
                    {BULK_COLUMN_DETAIL_KEYS.map((key) => (
                      <div key={key}>
                        <span className="font-medium text-gray-800">{BULK_EXPANDED_FIELD_LABELS[key]}:</span>{' '}
                        <span>{formatBulkDetailValue(bulkDetailsRow[key])}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
      </Modal>
      )}

      <Modal
        isOpen={showUniverseReplaceConfirm}
        onClose={closeUniverseReplaceConfirm}
        onEnter={confirmUniverseReplace}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>Confirm</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Are you sure you want to drop and reload the universe table for{' '}
                <span className="font-medium">{selectedOrganization}</span>?
              </p>
              <div className="flex justify-end">
                <Button type="button" variant="outline" onClick={confirmUniverseReplace}>
                  Continue
                </Button>
              </div>
            </CardContent>
      </Modal>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

export default FileUpload;

